import { useCallback, useEffect, useRef, useState } from 'react';
import {
  helpdeskAdminApi,
  type HelpdeskAccount,
  type HelpdeskAiAgent,
  type HelpdeskRule,
  type HelpdeskTag,
  type HelpdeskAccessUser,
  type SpaceOption,
} from '../../api/helpdesk-admin';
import { SablonyTab } from './SablonyTab';
import './helpdesk-settings.css';
import { confirmDialog, promptDialog } from '../../shell/dialogs';
import { Button } from '../../components/arc/button/button';
import { CompactSelect } from '../../components/arc/compact-select/compact-select';
import { ArcPage, ArcPageHeader, ArcPanel, ArcTabs } from '../../components/arc/primitives/arc-primitives';
import './helpdesk-settings-arc.css';

export type TabKey = 'schranky' | 'sablony' | 'pravidla' | 'ai' | 'tagy' | 'pristupy';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'schranky', label: 'Schránky' },
  { key: 'sablony', label: 'Email šablony' },
  { key: 'pravidla', label: 'Pravidla' },
  { key: 'ai', label: 'AI agenti' },
  { key: 'tagy', label: 'Tagy' },
  { key: 'pristupy', label: 'Přístupy' },
];

const LANG_LABEL: Record<string, string> = { cs: 'Čeština', sk: 'Slovenština', ro: 'Rumunština', en: 'Angličtina' };

function errText(e: unknown): string {
  const code = (e as { code?: string })?.code;
  return code ? `Chyba: ${code}` : 'Něco se nepovedlo';
}

export function HelpdeskSettings({
  onClose,
  initialTab = 'schranky',
  backLabel = 'Zpět na tickety',
}: {
  onClose: () => void;
  initialTab?: TabKey;
  backLabel?: string;
}) {
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [spaces, setSpaces] = useState<SpaceOption[]>([]);
  const [accounts, setAccounts] = useState<HelpdeskAccount[]>([]);

  useEffect(() => {
    helpdeskAdminApi.listSpaces().then(setSpaces).catch(() => setSpaces([]));
    helpdeskAdminApi.listAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  return (
    <ArcPage className="hn-wrap hn-arc-page">
      <ArcPageHeader eyebrow="HELPDESK" title="Nastavení podpory" description="Schránky, automatizace a přístup týmu na jednom místě." meta={<Button type="button" variant="ghost" size="sm" onClick={onClose}>{backLabel}</Button>} />
      <ArcTabs className="hn-arc-tabs" items={TABS.map((item) => ({ value: item.key, label: item.label }))} value={tab} onChange={setTab} label="Nastavení Helpdesku" />
      <div className="hn-tab-body">
        {tab === 'schranky' && <SchrankyTab spaces={spaces} />}
        {tab === 'sablony' && <SablonyTab accounts={accounts} />}
        {tab === 'pravidla' && <PravidlaTab spaces={spaces} />}
        {tab === 'ai' && <AiAgentiTab spaces={spaces} />}
        {tab === 'tagy' && <TagyTab />}
        {tab === 'pristupy' && <PristupyTab />}
      </div>
    </ArcPage>
  );
}

function SchrankyTab({ spaces }: { spaces: SpaceOption[] }) {
  const [rows, setRows] = useState<HelpdeskAccount[]>([]);
  const [editing, setEditing] = useState<HelpdeskAccount | 'new' | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    helpdeskAdminApi.listAccounts().then(setRows).catch((e) => setErr(errText(e)));
  }, []);
  useEffect(load, [load]);

  const spaceName = (id: number) => spaces.find((s) => s.id === id)?.name ?? `#${id}`;

  return (
    <div>
      <div className="hn-tab-actions">
        <Button type="button" variant="primary" size="sm" onClick={() => setEditing('new')}>Přidat schránku přes Google</Button>
      </div>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-list">
        {rows.map((a) => (
          <div className="hn-row" key={a.id}>
            <div>
              <div className="hn-name">{a.name}</div>
              <div className="hn-meta">{a.email}</div>
            </div>
            <span className="hn-pill">{spaceName(a.primary_space_id)}</span>
            <span className="hn-pill">{(LANG_LABEL[a.language] ?? a.language)}</span>
            <span className={`hn-pill ${a.oauth_connected ? 'on' : 'off'}`}>
              {a.oauth_connected ? '✓ Gmail připojen' : 'Gmail nepřipojen'}
            </span>
            <span className={`hn-pill ${a.mode === 'observer' ? 'observer' : 'on'}`}>
              {a.mode === 'observer' ? 'Pozorovatel' : 'Aktivní'}
            </span>
            <span className={`hn-pill ${a.ai_enabled ? 'on' : 'off'}`}>{a.ai_enabled ? 'AI on' : 'AI off'}</span>
            {!a.oauth_connected && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    const { authUrl } = await helpdeskAdminApi.startGmailOAuth(a.id);
                    window.location.href = authUrl;
                  } catch (e) {
                    const msg = e instanceof Error ? e.message : 'Chyba spuštění OAuth';
                    setErr(msg);
                  }
                }}
              >
                Připojit Gmail
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(a)}>Upravit</Button>
          </div>
        ))}
        {rows.length === 0 && !err && <div className="hn-empty">Zatím žádné schránky.</div>}
      </div>
      {editing && (
        <AccountForm
          spaces={spaces}
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AccountForm({
  spaces,
  account,
  onClose,
  onSaved,
}: {
  spaces: SpaceOption[];
  account: HelpdeskAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(account?.name ?? '');
  const [email, setEmail] = useState(account?.email ?? '');
  const [spaceId, setSpaceId] = useState<number>(account?.primary_space_id ?? spaces[0]?.id ?? 0);
  const [language, setLanguage] = useState(account?.language ?? 'cs');
  const [mode, setMode] = useState(account?.mode ?? 'active');
  const [aiEnabled, setAiEnabled] = useState(!!account?.ai_enabled);
  const [active, setActive] = useState(account ? !!account.active : true);
  const [signature, setSignature] = useState(account?.signature_default ?? '');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setErr('');
    const body = { name, email, primarySpaceId: spaceId, language, mode, aiEnabled, active, signatureDefault: signature };
    try {
      if (account) await helpdeskAdminApi.updateAccount(account.id, body);
      else await helpdeskAdminApi.createAccount(body);
      onSaved();
    } catch (e) { setErr(errText(e)); setSaving(false); }
  };

  return (
    <ArcPanel className="hn-card">
      <h6>{account ? 'Upravit schránku' : 'Nová schránka'}</h6>
      <FormRow label="Název (odesílatel)"><input value={name} onChange={(e) => setName(e.target.value)} /></FormRow>
      <FormRow label="Space">
        <CompactSelect className="hn-compact-select" value={String(spaceId)} onChange={(next) => setSpaceId(Number(next))} ariaLabel="Space" options={spaces.map((s) => ({ value: String(s.id), label: s.name }))} />
      </FormRow>
      <FormRow label="Email schránky"><input value={email} onChange={(e) => setEmail(e.target.value)} /></FormRow>
      <FormRow label="Jazyk">
        <CompactSelect className="hn-compact-select" value={language} onChange={setLanguage} ariaLabel="Jazyk" options={Object.entries(LANG_LABEL).map(([value, label]) => ({ value, label }))} />
      </FormRow>
      <FormRow label="Režim">
        <CompactSelect className="hn-compact-select" value={mode} onChange={setMode} ariaLabel="Režim" options={[{ value: 'active', label: 'Aktivní' }, { value: 'observer', label: 'Pozorovatel' }]} />
      </FormRow>
      <FormRow label="Podpis (výchozí)"><textarea rows={3} value={signature} onChange={(e) => setSignature(e.target.value)} /></FormRow>
      <FormRow label="AI povoleno"><input type="checkbox" checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} /></FormRow>
      <FormRow label="Aktivní"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /></FormRow>

      {account && (
        <div className="hn-oauth-status not-connected">
          Gmail napojení a Freshdesk import budou doplněny v samostatném kroku (fail-closed). Tlačítka jsou zatím vypnutá.
          <div className="hn-oauth-actions">
            <button type="button" disabled title="Připravujeme (Slice 6)">Spárovat Gmail</button>
            <button type="button" disabled title="Připravujeme (Slice 6)">Import z Freshdesku</button>
          </div>
        </div>
      )}

      {err && <div className="hn-err">{err}</div>}
      <div className="hn-form-buttons">
        <Button type="button" variant="primary" size="sm" disabled={saving} onClick={save}>Uložit</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Zrušit</Button>
      </div>
    </ArcPanel>
  );
}

const RULE_KINDS: { key: string; label: string }[] = [
  { key: '', label: 'Vše' },
  { key: 'acl', label: 'Přístupy' },
  { key: 'sop', label: 'Postupy (SOP)' },
  { key: 'ai_kb', label: 'AI knowledge' },
];

function PravidlaTab({ spaces }: { spaces: SpaceOption[] }) {
  const [spaceId, setSpaceId] = useState<number>(0);
  const [kind, setKind] = useState('');
  const [rows, setRows] = useState<HelpdeskRule[]>([]);
  const [selected, setSelected] = useState<HelpdeskRule | null>(null);
  const [err, setErr] = useState('');

  // Default to the Space of the current host subdomain (e.g. rondo.lvh.me → Rondo)
  // so the operator sees their own Space's rules first, not an empty foreign one.
  useEffect(() => {
    if (spaceId || spaces.length === 0) return;
    const sub = window.location.hostname.split('.')[0];
    const here = spaces.find((s) => s.slug === sub);
    setSpaceId((here ?? spaces[0]!).id);
  }, [spaces, spaceId]);

  const load = useCallback(() => {
    if (!spaceId) return;
    helpdeskAdminApi.listRules(spaceId, kind || undefined)
      .then((r) => { setRows(r); setSelected((cur) => r.find((x) => x.id === cur?.id) ?? null); })
      .catch((e) => setErr(errText(e)));
  }, [spaceId, kind]);
  useEffect(load, [load]);

  const addRule = async () => {
    const title = (await promptDialog({ title: 'Nové pravidlo', label: 'Název pravidla', confirmLabel: 'Vytvořit' }))?.trim();
    if (!title) return;
    try {
      const { id } = await helpdeskAdminApi.createRule({ spaceId, kind: kind || 'ai_kb', title, bodyHtml: '<p></p>' });
      const fresh = await helpdeskAdminApi.listRules(spaceId, kind || undefined);
      setRows(fresh);
      setSelected(fresh.find((r) => r.id === id) ?? null);
    } catch (e) { setErr(errText(e)); }
  };

  return (
    <div>
      <div className="hn-rules-spaces">
        {spaces.map((s) => (
          <button key={s.id} type="button" className={`hn-rules-space${s.id === spaceId ? ' active' : ''}`} onClick={() => setSpaceId(s.id)}>
            {s.name}
          </button>
        ))}
      </div>
      <div className="hn-rules-kinds">
        {RULE_KINDS.map((k) => (
          <button key={k.key} type="button" className={`hn-kind-chip${k.key === kind ? ' active' : ''}`} onClick={() => setKind(k.key)}>
            {k.label}
          </button>
        ))}
      </div>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-rules-grid">
        <div>
          <Button type="button" variant="primary" size="sm" className="hn-rules-add" onClick={addRule}>Nové pravidlo</Button>
          <div className="hn-rules-list">
            {rows.map((r, i) => (
              <button
                key={r.id}
                type="button"
                className={`hn-rules-item${r.id === selected?.id ? ' active' : ''}${r.active ? '' : ' inactive'}`}
                onClick={() => setSelected(r)}
              >
                <span className="hn-rules-num">{i + 1}</span>
                <span>
                  <span className="hn-rules-title">{r.title}</span>
                  <span className="hn-meta"> · {r.slug}{r.active ? '' : ' (vypnuto)'}</span>
                </span>
              </button>
            ))}
            {rows.length === 0 && <div className="hn-empty">Žádná pravidla pro tento výběr.</div>}
          </div>
        </div>
        <div>
          {selected
            ? <RuleEditor key={selected.id} rule={selected} onSaved={load} onDeleted={() => { setSelected(null); load(); }} />
            : <div className="hn-empty">Vyber nebo vytvoř pravidlo.</div>}
        </div>
      </div>
    </div>
  );
}

function RuleEditor({ rule, onSaved, onDeleted }: { rule: HelpdeskRule; onSaved: () => void; onDeleted: () => void }) {
  const [title, setTitle] = useState(rule.title);
  const [kind, setKind] = useState(rule.kind);
  const [active, setActive] = useState(!!rule.active);
  const [status, setStatus] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);

  const save = async () => {
    setStatus('Ukládám…');
    try {
      await helpdeskAdminApi.updateRule(rule.id, {
        title, kind, active, bodyHtml: bodyRef.current?.innerHTML ?? '',
      });
      setStatus('✓ Uloženo');
      onSaved();
    } catch (e) { setStatus(errText(e)); }
  };
  const del = async () => {
    if (!(await confirmDialog({ title: 'Smazat pravidlo?', message: 'Pravidlo se deaktivuje.', confirmLabel: 'Smazat', danger: true }))) return;
    try { await helpdeskAdminApi.deleteRule(rule.id); onDeleted(); } catch (e) { setStatus(errText(e)); }
  };
  const cmd = (c: string) => (e: React.MouseEvent) => { e.preventDefault(); document.execCommand(c); };

  return (
    <ArcPanel className="hn-card">
      <input className="hn-rule-title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="hn-form-inline">
        <CompactSelect className="hn-compact-select" value={kind} onChange={setKind} ariaLabel="Typ pravidla" options={[{ value: 'acl', label: 'Přístup (ACL)' }, { value: 'sop', label: 'Postup (SOP)' }, { value: 'ai_kb', label: 'AI knowledge' }]} />
        <label className="hn-check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Aktivní</label>
      </div>
      <div className="hn-tt-toolbar">
        <button type="button" title="Tučně" onMouseDown={cmd('bold')}><strong>B</strong></button>
        <button type="button" title="Kurzíva" onMouseDown={cmd('italic')}><em>I</em></button>
        <button type="button" title="Seznam" onMouseDown={cmd('insertUnorderedList')}>• Seznam</button>
        <button type="button" title="Číslovaný" onMouseDown={cmd('insertOrderedList')}>1. Seznam</button>
      </div>
      <div ref={bodyRef} className="hn-tt-editor" contentEditable suppressContentEditableWarning dangerouslySetInnerHTML={{ __html: rule.body_html ?? '' }} />
      <div className="hn-form-buttons">
        <Button type="button" variant="primary" size="sm" onClick={save}>Uložit</Button>
        <Button type="button" variant="danger" size="sm" onClick={del}>Smazat</Button>
        <span className="hn-rule-status">{status}</span>
      </div>
    </ArcPanel>
  );
}

const AUTONOMY: { key: string; label: string }[] = [
  { key: 'review', label: 'Review - každý návrh AI čeká na schválení operátorem' },
  { key: 'copilot', label: 'Co-pilot - AI sama pošle, pokud confidence ≥ threshold' },
  { key: 'autopilot', label: 'Autopilot - AI sama pošle vždy (kromě tagů ai_autopilot=off)' },
];

function AiAgentiTab({ spaces }: { spaces: SpaceOption[] }) {
  const [rows, setRows] = useState<HelpdeskAiAgent[]>([]);
  const [editing, setEditing] = useState<HelpdeskAiAgent | 'new' | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => { helpdeskAdminApi.listAiAgents().then(setRows).catch((e) => setErr(errText(e))); }, []);
  useEffect(load, [load]);

  return (
    <div>
      <div className="hn-tab-actions"><Button type="button" variant="primary" size="sm" onClick={() => setEditing('new')}>Nový AI agent</Button></div>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-list">
        {rows.map((a) => (
          <div className="hn-row" key={a.id}>
            <div>
              <div className="hn-name">{a.name} <code className="hn-code">{a.slug}</code></div>
              <div className="hn-meta">{(a.signature ?? '').slice(0, 80)}</div>
            </div>
            <span className={`hn-pill ${a.autonomy_mode === 'autopilot' ? 'on' : a.autonomy_mode === 'copilot' ? 'warn' : ''}`}>
              {a.autonomy_mode === 'autopilot' ? 'Autopilot' : a.autonomy_mode === 'copilot' ? `Co-pilot (≥${a.autopilot_min_confidence})` : 'Review'}
            </span>
            <span className={`hn-pill ${a.active ? 'on' : 'off'}`}>{a.active ? 'aktivní' : 'neaktivní'}</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(a)}>Upravit</Button>
          </div>
        ))}
        {rows.length === 0 && !err && <div className="hn-empty">Zatím žádní AI agenti.</div>}
      </div>
      {editing && (
        <AiAgentForm
          spaces={spaces}
          agent={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AiAgentForm({ agent, onClose, onSaved }: { spaces: SpaceOption[]; agent: HelpdeskAiAgent | null; onClose: () => void; onSaved: () => void }) {
  const [slug, setSlug] = useState(agent?.slug ?? '');
  const [name, setName] = useState(agent?.name ?? '');
  const [signature, setSignature] = useState(agent?.signature ?? '');
  const [autonomy, setAutonomy] = useState(agent?.autonomy_mode ?? 'review');
  const [conf, setConf] = useState(Number(agent?.autopilot_min_confidence ?? 0.85));
  const [active, setActive] = useState(agent ? !!agent.active : true);
  const [err, setErr] = useState('');

  const save = async () => {
    setErr('');
    const body = { name, signature, autonomyMode: autonomy, autopilotMinConfidence: conf, active };
    try {
      if (agent) await helpdeskAdminApi.updateAiAgent(agent.id, body);
      else await helpdeskAdminApi.createAiAgent({ ...body, slug });
      onSaved();
    } catch (e) { setErr(errText(e)); }
  };

  return (
    <ArcPanel className="hn-card">
      <h6>{agent ? 'Upravit AI agenta' : 'Nový AI agent'}</h6>
      <FormRow label="Slug"><input value={slug} disabled={!!agent} placeholder="ai-asistent" onChange={(e) => setSlug(e.target.value)} /></FormRow>
      <FormRow label="Jméno"><input value={name} placeholder="AI asistent" onChange={(e) => setName(e.target.value)} /></FormRow>
      <FormRow label="Podpis"><textarea rows={3} value={signature} onChange={(e) => setSignature(e.target.value)} /></FormRow>
      <FormRow label="Autonomie">
        <CompactSelect className="hn-compact-select" value={autonomy} onChange={setAutonomy} ariaLabel="Autonomie" options={AUTONOMY.map((a) => ({ value: a.key, label: a.label }))} />
      </FormRow>
      {autonomy === 'copilot' && (
        <FormRow label="Min. confidence">
          <span className="hn-slider"><input type="range" min={0.5} max={1} step={0.01} value={conf} onChange={(e) => setConf(Number(e.target.value))} /> {conf.toFixed(2)}</span>
        </FormRow>
      )}
      <FormRow label="Aktivní"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /></FormRow>
      <div className="hn-info-box">Recall okno 24 h - po každém auto-sendu má operátor 24 hodin na recall odpovědi (živé odesílání přijde fail-closed v Slice 6).</div>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-form-buttons">
        <Button type="button" variant="primary" size="sm" onClick={save}>Uložit</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Zrušit</Button>
      </div>
    </ArcPanel>
  );
}

const TAG_AUTOPILOT: { key: string; label: string }[] = [
  { key: 'auto', label: 'Auto - řídí nastavení AI agenta (default)' },
  { key: 'off', label: 'Off - vždy review (citlivý tag)' },
  { key: 'on', label: 'On - vždy autopilot' },
];

function TagyTab() {
  const [rows, setRows] = useState<HelpdeskTag[]>([]);
  const [editing, setEditing] = useState<HelpdeskTag | 'new' | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => { helpdeskAdminApi.listTags().then(setRows).catch((e) => setErr(errText(e))); }, []);
  useEffect(load, [load]);

  const del = async (t: HelpdeskTag) => {
    if (!(await confirmDialog({ title: 'Smazat tag?', message: `Tag „${t.name}“ se smaže.`, confirmLabel: 'Smazat', danger: true }))) return;
    try { await helpdeskAdminApi.deleteTag(t.id); load(); } catch (e) { setErr(errText(e)); }
  };

  return (
    <div>
      <div className="hn-tab-actions"><Button type="button" variant="primary" size="sm" onClick={() => setEditing('new')}>Přidat tag</Button></div>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-list">
        {rows.map((t) => (
          <div className="hn-row" key={t.id}>
            <div>
              <div className="hn-name"><span className="hn-tag-dot" style={{ background: t.color }} /> {t.name}</div>
              <div className="hn-meta"><code>{t.slug}</code>{t.description ? ` · ${t.description}` : ''}</div>
            </div>
            <span className="hn-pill">{t.usage_count}× použito</span>
            <span className={`hn-pill ${t.ai_autopilot === 'on' ? 'on' : t.ai_autopilot === 'off' ? 'off' : ''}`}>
              {t.ai_autopilot === 'on' ? 'on' : t.ai_autopilot === 'off' ? 'off' : 'auto'}
            </span>
            {!t.active && <span className="hn-pill off">neaktivní</span>}
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(t)}>Upravit</Button>
            {t.active ? <Button type="button" variant="danger" size="sm" onClick={() => del(t)}>Smazat</Button> : <span />}
          </div>
        ))}
        {rows.length === 0 && !err && <div className="hn-empty">Zatím žádné tagy.</div>}
      </div>
      {editing && (
        <TagForm tag={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function TagForm({ tag, onClose, onSaved }: { tag: HelpdeskTag | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(tag?.name ?? '');
  const [slug, setSlug] = useState(tag?.slug ?? '');
  const [color, setColor] = useState(tag?.color ?? '#6b7280');
  const [description, setDescription] = useState(tag?.description ?? '');
  const [sortOrder, setSortOrder] = useState(tag?.sort_order ?? 100);
  const [aiAutopilot, setAiAutopilot] = useState(tag?.ai_autopilot ?? 'auto');
  const [active, setActive] = useState(tag ? !!tag.active : true);
  const [err, setErr] = useState('');

  const save = async () => {
    setErr('');
    const body = { name, color, description, sortOrder, aiAutopilot, active };
    try {
      if (tag) await helpdeskAdminApi.updateTag(tag.id, body);
      else await helpdeskAdminApi.createTag({ ...body, slug });
      onSaved();
    } catch (e) { setErr(errText(e)); }
  };

  return (
    <ArcPanel className="hn-card">
      <h6>{tag ? 'Upravit tag' : 'Nový tag'}</h6>
      <FormRow label="Název"><input value={name} placeholder="Heslo / přihlášení" onChange={(e) => setName(e.target.value)} /></FormRow>
      {!tag && <FormRow label="Slug"><input value={slug} placeholder="heslo (volitelné)" onChange={(e) => setSlug(e.target.value)} /></FormRow>}
      <FormRow label="Barva"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} /></FormRow>
      <FormRow label="Popis"><input value={description} placeholder="Pomáhá operátorovi" onChange={(e) => setDescription(e.target.value)} /></FormRow>
      <FormRow label="Pořadí"><input type="number" min={0} value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} /></FormRow>
      <FormRow label="AI policy">
        <CompactSelect className="hn-compact-select" value={aiAutopilot} onChange={setAiAutopilot} ariaLabel="AI policy" options={TAG_AUTOPILOT.map((a) => ({ value: a.key, label: a.label }))} />
      </FormRow>
      {tag && <FormRow label="Aktivní"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /></FormRow>}
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-form-buttons">
        <Button type="button" variant="primary" size="sm" onClick={save}>Uložit</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Zrušit</Button>
      </div>
    </ArcPanel>
  );
}

function PristupyTab() {
  const [rows, setRows] = useState<HelpdeskAccessUser[]>([]);
  const [err, setErr] = useState('');
  useEffect(() => { helpdeskAdminApi.listAccessUsers().then(setRows).catch((e) => setErr(errText(e))); }, []);

  return (
    <div>
      <div className="hn-info-box">
        Přístup do Helpdesku se v Platform 1 řídí přes role/oprávnění (RBAC), ne přepínačem u uživatele. Tady je přehled,
        kdo aktuálně drží <code>helpdesk:read</code>. Udělování/odebírání přístupu přijde jako samostatný RBAC krok.
      </div>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-list">
        {rows.map((u) => (
          <div className="hn-row" key={u.id}>
            <div>
              <div className="hn-name">{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}</div>
              <div className="hn-meta">{u.email} · {u.role_keys.join(', ') || 'bez role'}</div>
            </div>
            <span className={`hn-pill ${u.has_access ? 'on' : 'off'}`}>{u.has_access ? '✓ má přístup' : 'bez přístupu'}</span>
          </div>
        ))}
        {rows.length === 0 && !err && <div className="hn-empty">Žádní uživatelé.</div>}
      </div>
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="hn-form-row">
      <label>{label}</label>
      <div>{children}</div>
    </div>
  );
}
